import React, { useState, useEffect, useRef } from "react";
import { useGoogleMaps } from "react-hook-google-maps";
import { DatePicker, Button, Slider, Input, message, Form } from "antd";
import { LockOutlined, PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import "./LocationHistory.css";

const { RangePicker } = DatePicker;

// Dark theme for Google Maps
const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

function InnerMap({ apiKey, data }) {
  const { ref, map, google } = useGoogleMaps(apiKey, {
    zoom: 12,
    center: { lat: 37.4, lng: -122.1 },
    styles: darkMapStyle,
    disableDefaultUI: true,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playIntervalRef = useRef(null);
  
  // Refs for map objects
  const markerRef = useRef(null);
  const polylineRef = useRef(null);

  useEffect(() => {
    if (!map || !google || data.length === 0) return;

    // Sort data chronologically (oldest to newest)
    const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const pathCoordinates = sortedData.map((pt) => ({ lat: pt.lat, lng: pt.lng }));

    // Center map on latest location
    if (sortedData.length > 0) {
        const lastPoint = sortedData[sortedData.length - 1];
        map.setCenter({ lat: lastPoint.lat, lng: lastPoint.lng });
    }

    // Clean up previous markers/lines
    if (markerRef.current) markerRef.current.setMap(null);
    if (polylineRef.current) polylineRef.current.setMap(null);

    // Draw full path
    polylineRef.current = new google.maps.Polyline({
      path: pathCoordinates,
      geodesic: true,
      strokeColor: "#00e676",
      strokeOpacity: 0.5,
      strokeWeight: 4,
    });
    polylineRef.current.setMap(map);

    // Create moving marker
    markerRef.current = new google.maps.Marker({
      position: pathCoordinates[0] || { lat: 37.4, lng: -122.1 },
      map: map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#ffffff",
        fillOpacity: 1,
        strokeColor: "#00e676",
        strokeWeight: 2,
      },
    });

  }, [data, map, google]);

  useEffect(() => {
    if (!markerRef.current || data.length === 0) return;
    
    // Sort chronological again
    const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    if (currentIndex >= 0 && currentIndex < sortedData.length) {
        const currentPoint = sortedData[currentIndex];
        markerRef.current.setPosition({ lat: currentPoint.lat, lng: currentPoint.lng });
    }
  }, [currentIndex, data]);


  const togglePlay = () => {
    if (isPlaying) {
      clearInterval(playIntervalRef.current);
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      if (currentIndex >= data.length - 1) {
          setCurrentIndex(0);
      }
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= data.length - 1) {
            clearInterval(playIntervalRef.current);
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 500); // adjust speed
    }
  };

  useEffect(() => {
      return () => {
          if (playIntervalRef.current) clearInterval(playIntervalRef.current);
      };
  }, []);

  const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const currentPoint = sortedData[currentIndex];

  return (
    <>
      <div ref={ref} className="map-wrapper" />
      <div className="glass-panel">
        <div className="controls-row" style={{justifyContent: "space-between", alignItems: "center"}}>
            <Button 
                type="primary" 
                shape="circle" 
                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />} 
                size="large"
                onClick={togglePlay}
                style={{ backgroundColor: '#00e676', borderColor: '#00e676', color: '#000' }}
                disabled={data.length === 0}
            />
            <div style={{flexGrow: 1, margin: "0 24px"}}>
                <Slider 
                    min={0} 
                    max={data.length > 0 ? data.length - 1 : 0} 
                    value={currentIndex} 
                    onChange={setCurrentIndex}
                    tipFormatter={(val) => {
                        return sortedData[val]?.created_at || "";
                    }}
                    disabled={data.length === 0}
                />
            </div>
            
        </div>
        <div className="info-display">
            {currentPoint ? 
               `${currentPoint.created_at} | Lat: ${currentPoint.lat.toFixed(4)}, Lng: ${currentPoint.lng.toFixed(4)} | H: ${currentPoint.height.toFixed(1)}m` 
               : "No data points selected."}
        </div>
      </div>
    </>
  );
}

export default function LocationHistory() {
  const [apiKey, setApiKey] = useState("");
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(true);

  // Authenticate map view separately
  const handleUnlock = (values) => {
    // Simple password check
    if (values.password === "tczhong-map") {
      setIsLocked(false);
      message.success("Access Granted");
    } else {
      message.error("Incorrect Password");
    }
  };

  useEffect(() => {
    if (!isLocked && !apiKey) {
      axios.get("https://api.tczhong.com/backend/api?name=google_map").then((res) => {
        if (res.data && res.data.data) {
          setApiKey(res.data.data);
        }
      }).catch(err => {
        message.error("Failed to load Google Maps API key");
        console.error(err);
      });
    }
  }, [isLocked, apiKey]);

  const fetchData = (dates, dateStrings) => {
    if (!dates || dates.length < 2) return;
    setLoading(true);
    // e.g. "2026-03-14 00:00:00"
    const startObj = dates[0].startOf('day').format("YYYY-MM-DD HH:mm:ss");
    const endObj = dates[1].endOf('day').format("YYYY-MM-DD HH:mm:ss");
    
    axios.get(`https://api.tczhong.com/backend/location?start=${startObj}&end=${endObj}&limit=10000`)
      .then((res) => {
        if (res.data && res.data.status === "success" && res.data.data) {
          setLocationData(res.data.data);
          message.success(`Loaded ${res.data.data.length} location points`);
        } else {
           setLocationData([]);
           message.warning("No location data found in this range.");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to load location data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (isLocked) {
    return (
      <div className="location-history-container">
        <div className="password-lock-screen">
          <LockOutlined className="lock-icon" />
          <h2 style={{color: 'white'}}>Protected Area</h2>
          <Form onFinish={handleUnlock} className="password-form">
            <Form.Item 
                name="password" 
                rules={[{ required: true, message: 'Please input the password!' }]}
                style={{marginBottom: 0}}
            >
              <Input.Password placeholder="Enter Password" size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" style={{ width: '100%', marginTop: '16px', background: '#00e676', borderColor: '#00e676', color: '#000' }}>
              Unlock
            </Button>
          </Form>
        </div>
      </div>
    );
  }

  return (
    <div className="location-history-container">
      {/* Absolute positioning map wrapper inside Container */}
      {apiKey && <InnerMap apiKey={apiKey} data={locationData} />}
      
      {/* Date picker overlaid at top */}
      <div style={{ position: "absolute", top: 24, zIndex: 10, left: '50%', transform: 'translateX(-50%)' }}>
          <RangePicker 
            showTime 
            onChange={fetchData} 
            style={{ 
                background: "rgba(30, 30, 30, 0.7)", 
                backdropFilter: "blur(8px)", 
                border: "1px solid rgba(255,255,255,0.2)",
                boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
                borderRadius: '8px'
            }}
          />
      </div>
    </div>
  );
}
